import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  AdditionalItem,
  AssociatedClient,
  Client,
  Movement,
  MovementAdditionalItemDraft,
} from "types";
import CustomNumberInput from "components/CustomNumberInput";
import { RxPlus } from "react-icons/rx";
import { MdDelete } from "react-icons/md";
import { getAdditionalItemPriceForClient , getOriginMovementLabel } from "helpers/additionalMovements";

const EMPTY_INITIAL_ITEMS: MovementAdditionalItemDraft[] = [];

type Props = {
  open: boolean;
  title: string;
  movement: Movement;
  additionalItems: AdditionalItem[];
  childClients: Client[];
  associatedClients: AssociatedClient[];
  initialItems?: MovementAdditionalItemDraft[];
  embedded?: boolean;
  emptyConfirmLabel?: string;
  confirmLabel?: string;
  onChange?: (payload: MovementAdditionalItemsConfirmPayload) => void;
  onClose: () => void;
  onConfirm: (payload: MovementAdditionalItemsConfirmPayload) => void;
};

export type MovementAdditionalItemsConfirmPayload = {
  movement: Movement;
  items: MovementAdditionalItemDraft[];
};

export default function MovementAdditionalItemsStepModal({
  open,
  title,
  movement,
  additionalItems,
  childClients,
  associatedClients,
  initialItems = EMPTY_INITIAL_ITEMS,
  embedded = false,
  emptyConfirmLabel = "Continuar sin adicionales",
  confirmLabel = "Guardar movimiento",
  onChange,
  onClose,
  onConfirm,
}: Props) {
  const [headerMovement, setHeaderMovement] = useState<Movement>({
    ...movement,
    movement_id: movement.id ?? movement.movement_id ?? null,
  });
  const [selectedChildClient, setSelectedChildClient] = useState<Client | null>(
    null,
  );
  const [selectedAdditionalItem, setSelectedAdditionalItem] =
    useState<AdditionalItem | null>(null);
  const [amount, setAmount] = useState(1);
  const [price, setPrice] = useState<number | "">("");
  const [rows, setRows] = useState<MovementAdditionalItemDraft[]>([]);

  useEffect(() => {
    setHeaderMovement({
      ...movement,
      movement_id: movement.id ?? movement.movement_id ?? null,
      observation: movement.observation ?? "",
      associated_client: movement.associated_client ?? "",
      client_document: movement.client_document ?? "",
      numero_remito: movement.numero_remito ?? "",
    });
  }, [movement]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setRows(
      initialItems.map((item) => {
        const lockedClientId =
          item.additional_item?.client_id ??
          item.client_id ??
          null;

        const lockedChildClient = childClients.find(
          (client) => client.id === Number(lockedClientId),
        );

        if (!lockedChildClient) {
          return item;
        }

        return {
          ...item,
          line_client_id: lockedChildClient.id,
          line_client_name: lockedChildClient.razon_social,
        };
      }),
    );
  }, [open, initialItems, childClients]);

  const getLockedLineClientId = (row: MovementAdditionalItemDraft) => {
    const lockedClientId =
      row.additional_item?.client_id ??
      row.client_id ??
      null;

    if (lockedClientId == null) {
      return null;
    }

    return childClients.some((client) => client.id === Number(lockedClientId))
      ? Number(lockedClientId)
      : null;
  };

  const emitChange = (
    nextMovement: Movement,
    nextRows: MovementAdditionalItemDraft[],
  ) => {
    if (embedded) {
      onChange?.({
        movement: nextMovement,
        items: nextRows,
      });
    }
  };

  const updateHeaderMovement = (
    updater: (prev: Movement) => Movement,
  ) => {
    setHeaderMovement((prev) => {
      const nextMovement = updater(prev);

      emitChange(nextMovement, rows);

      return nextMovement;
    });
  };

  const updateRows = (
    updater:
      | MovementAdditionalItemDraft[]
      | ((prev: MovementAdditionalItemDraft[]) => MovementAdditionalItemDraft[]),
  ) => {
    setRows((prev) => {
      const nextRows =
        typeof updater === "function" ? updater(prev) : updater;

      emitChange(headerMovement, nextRows);

      return nextRows;
    });
  };

  const availableAdditionalItems = useMemo(
    () =>
      additionalItems.filter(
        (item) =>
          item.client_id == null ||
          item.client_id === movement.client_id ||
          item.client_id === selectedChildClient?.id,
      ),
    [additionalItems, movement.client_id, selectedChildClient],
  );

  const handleAddRow = () => {
    if (!selectedChildClient || !selectedAdditionalItem || amount <= 0) {
      return;
    }

    updateRows((prev) => [
      {
        id: `${selectedChildClient.id}-${selectedAdditionalItem.id}-${prev.length}`,
        additional_item_id: selectedAdditionalItem.id,
        description: selectedAdditionalItem.description,
        amount,
        price: Number(price || getAdditionalItemPriceForClient(selectedAdditionalItem, selectedChildClient)),
        client_id: selectedAdditionalItem.client_id,
        line_client_id: selectedChildClient.id,
        line_client_name: selectedChildClient.razon_social,
        additional_item: selectedAdditionalItem,
      },
      ...prev,
    ]);

    setSelectedAdditionalItem(null);
    setAmount(1);
    setPrice("");
  };

  const handleRowChange = (
    id: number | string,
    field: "amount" | "price",
    value: string | number,
  ) => {
    updateRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: Number(value),
            }
          : row,
      ),
    );
  };

  const handleDeleteRow = (id: number | string) => {
    updateRows((prev) => prev.filter((row) => row.id !== id));
  };

  const content = (
    <>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {!embedded && (
          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                disabled
                label="Movimiento origen asociado"
                value={getOriginMovementLabel(movement)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha del movimiento adicional"
                value={headerMovement.date?.slice(0, 10) ?? ""}
                onChange={(event) =>
                  updateHeaderMovement((prev) => ({
                    ...prev,
                    date: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                options={associatedClients}
                getOptionLabel={(option) => option.razon_social}
                value={
                  associatedClients.find(
                    (client) =>
                      client.razon_social === headerMovement.associated_client,
                  ) ?? null
                }
                onChange={(_, value) =>
                  updateHeaderMovement((prev) => ({
                    ...prev,
                    associated_client: value?.razon_social ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente asociado del movimiento adicional"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Documento del cliente del movimiento adicional"
                value={headerMovement.client_document ?? ""}
                onChange={(event) =>
                  updateHeaderMovement((prev) => ({
                    ...prev,
                    client_document: event.target.value,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Remito del movimiento adicional"
                value={headerMovement.numero_remito ?? ""}
                onChange={(event) =>
                  updateHeaderMovement((prev) => ({
                    ...prev,
                    numero_remito: event.target.value,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Observaciones del movimiento adicional"
                value={headerMovement.observation ?? ""}
                onChange={(event) =>
                  updateHeaderMovement((prev) => ({
                    ...prev,
                    observation: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Autocomplete
            options={childClients}
            getOptionLabel={(option) => option.razon_social}
            value={selectedChildClient}
            onChange={(_, value) => {
              setSelectedChildClient(value);
              updateHeaderMovement((prev) => ({
                ...prev,
                client_id: value?.id ?? movement.client_id,
              }));
              setPrice(getAdditionalItemPriceForClient(selectedAdditionalItem, value));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Línea" required />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            options={availableAdditionalItems}
            getOptionLabel={(option) => option.description}
            value={selectedAdditionalItem}
            onChange={(_, value) => {
              setSelectedAdditionalItem(value);
              setPrice(getAdditionalItemPriceForClient(value, selectedChildClient));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Adicional" required />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <CustomNumberInput
            label="Cantidad"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            required
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <CustomNumberInput
            label="Precio"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            required
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={1}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Button
            variant="contained"
            onClick={handleAddRow}
            disabled={!selectedChildClient || !selectedAdditionalItem}
            sx={{ minWidth: 48, height: 40 }}
          >
            <RxPlus size="1.2rem" />
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "30%" }}>Línea</TableCell>
              <TableCell sx={{ width: "30%" }}>Adicional</TableCell>
              <TableCell sx={{ width: "16%" }}>Cantidad</TableCell>
              <TableCell sx={{ width: "16%" }}>Precio</TableCell>
              <TableCell sx={{ width: "8%" }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const lockedLineClientId = getLockedLineClientId(row);
              const lineClientId = lockedLineClientId ?? row.line_client_id;

              return (
                <TableRow key={String(row.id)}>
                  {embedded && (
                    <TableCell>
                      <Autocomplete
                        options={childClients}
                        getOptionLabel={(option) => option.razon_social}
                        value={
                          childClients.find(
                            (client) => client.id === Number(lineClientId),
                          ) ?? null
                        }
                        disabled={lockedLineClientId != null}
                        onChange={(_, value) => {
                          if (lockedLineClientId != null) {
                            return;
                          }

                          updateRows((prev) =>
                            prev.map((currentRow) =>
                              currentRow.id === row.id
                                ? {
                                    ...currentRow,
                                    line_client_id: value?.id ?? 0,
                                    line_client_name: value?.razon_social ?? "Seleccionar línea",
                                  }
                                : currentRow,
                            ),
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Línea" required />
                        )}
                      />
                    </TableCell>
                  )}
                  {!embedded && (<TableCell>{row.line_client_name}</TableCell>)}
                  <TableCell>{row.description}</TableCell>
                  <TableCell>
                    <CustomNumberInput
                      label="Cantidad"
                      value={row.amount}
                      onChange={(e) =>
                        handleRowChange(row.id, "amount", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <CustomNumberInput
                      label="Precio"
                      value={row.price ?? 0}
                      onChange={(e) =>
                        handleRowChange(row.id, "price", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteRow(row.id)}>
                      <MdDelete />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay adicionales para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>{content}</DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={() =>
            onConfirm({
              movement: headerMovement,
              items: [],
            })
          }
        >
          {emptyConfirmLabel}
        </Button>

        <Button
          variant="contained"
          onClick={() =>
            onConfirm({
              movement: headerMovement,
              items: rows,
            })
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
